import { useDeferredValue, useState } from 'react';
import { Link, useNavigate, useOutletContext } from 'react-router-dom';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import { useDocumentTitle, useTid } from '../lib/hooks';
import { fmtDayMon, TIPO_LABEL } from '../lib/format';
import type { RestaurantRow, TenantInfo } from '../lib/types';
import { Button, ErrorState, Loading, NotaBadge } from '../components/ui';
import { RestaurantForm } from '../components/RestaurantForm';

export function Restaurants() {
  const tid = useTid();
  const info = useOutletContext<TenantInfo>();
  const nav = useNavigate();
  useDocumentTitle('Restaurantes');
  const [query, setQuery] = useState('');
  const [creating, setCreating] = useState(false);
  const q = useDeferredValue(query.trim());
  const [arquivados, setArquivados] = useState(false);
  const list = useQuery({
    queryKey: ['restaurants', tid, q, arquivados ? 'arq' : ''],
    queryFn: () => {
      const p = new URLSearchParams();
      if (q) p.set('q', q);
      if (arquivados) p.set('arquivados', '1');
      return api.get<RestaurantRow[]>(`/api/t/${tid}/restaurants${p.toString() ? `?${p}` : ''}`);
    },
    placeholderData: keepPreviousData,
  });

  return (
    <div>
      <div className="row gap-10" style={{ marginBottom: 14 }}>
        <h1 className="h1 flex1">Restaurantes</h1>
        {info.can.createRestaurant && <Button variant="accent" size="sm" onClick={() => setCreating(true)}>+ Novo</Button>}
      </div>
      <input
        className="input"
        type="search"
        style={{ height: 42, marginBottom: 14, background: '#fff', borderRadius: 10, fontSize: 14 }}
        placeholder="Buscar por nome ou CNPJ"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        aria-label="Buscar restaurante"
      />
      <div className="chips" style={{ marginTop: -4 }}>
        <button className={`chip sm ${!arquivados ? 'on' : ''}`} onClick={() => setArquivados(false)} aria-pressed={!arquivados}>Ativos</button>
        <button className={`chip sm ${arquivados ? 'on' : ''}`} onClick={() => setArquivados(true)} aria-pressed={arquivados}>Arquivados</button>
      </div>
      {list.error && <ErrorState message="Não foi possível carregar os restaurantes." onRetry={() => list.refetch()} />}
      {list.isLoading && <Loading rows={4} height={120} />}
      <div className="grid-cards">
        {list.data?.map((r) => (
          <Link key={r.id} to={`/t/${tid}/restaurantes/${r.id}`} className="item" style={{ display: 'block', padding: 15, borderRadius: 13 }}>
            <div className="row gap-10" style={{ alignItems: 'flex-start' }}>
              <div className="flex1">
                <div style={{ fontSize: 15, fontWeight: 600, letterSpacing: '-.01em', marginBottom: 3 }}>{r.nome}</div>
                <div className="mono" style={{ fontSize: 11.5, color: 'var(--text-2)' }}>{r.cnpj ?? '—'}</div>
              </div>
              <div className="none stack gap-4" style={{ alignItems: 'flex-end' }}>
                <span className="pill neutral">{TIPO_LABEL[r.tipo]}</span>
                <NotaBadge nota={r.nota} showClasse />
              </div>
            </div>
            <div className="row wrap divider-top" style={{ marginTop: 12, paddingTop: 12, gap: '4px 18px', alignItems: 'flex-start' }}>
              <div>
                <div className="k">Nutricionista</div>
                <div style={{ fontSize: 12.5, fontWeight: 500 }}>{r.nutriNome ?? '—'}</div>
              </div>
              <div>
                <div className="k">Última visita</div>
                <div className="mono" style={{ fontSize: 12.5, fontWeight: 500 }}>{r.lastVisitAt ? fmtDayMon(r.lastVisitAt) : '—'}</div>
              </div>
              <div>
                <div className="k">Pendências</div>
                <div className="mono" style={{ fontSize: 12.5, fontWeight: 500, color: r.openNcs ? 'var(--nc)' : 'var(--resolved)' }}>{r.openNcs}</div>
              </div>
            </div>
          </Link>
        ))}
      </div>
      {list.data && list.data.length === 0 && (
        <div className="empty plain" style={{ padding: '34px 16px' }}>
          {arquivados ? 'Nenhum restaurante arquivado.' : q ? 'Nenhum restaurante encontrado para esta busca.' : info.can.createRestaurant ? 'Nenhum restaurante ainda. Cadastre o primeiro para começar as visitas.' : 'Nenhum restaurante liberado para você ainda.'}
        </div>
      )}
      <RestaurantForm tid={tid} open={creating} onClose={() => setCreating(false)} onSaved={(id) => nav(`/t/${tid}/restaurantes/${id}`)} />
    </div>
  );
}
